import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  TbCalendar,
  TbChevronDown,
  TbHeadset,
  TbInfoCircle,
  TbSearch,
  TbUser,
  TbX,
} from "react-icons/tb";
import { ScreenGreeting } from "../../../Components/Molecules";
import { useIbProductFeaturesStore } from "../../../Stores/ibProductFeaturesStore";
import { isIbProductEnabled } from "../../../Utils/ibProductFeatures";

const MOCK_CONSULTANTS = [
  { id: "1", name: "Ana Costa", role: "Consultora Premium" },
  { id: "2", name: "Ricardo Alves", role: "Especialista em Pix" },
  { id: "3", name: "Mariana Silva", role: "Suporte Conta" },
];

type FaqItem = { question: string; to?: string; secondary?: string };
type FaqCat = { title: string; description?: string; items: FaqItem[] };

export const Support = () => {
  const [faqQuery, setFaqQuery] = React.useState("");
  const [tab, setTab] = React.useState<0 | 1>(0);
  const [selectedConsultant, setSelectedConsultant] = React.useState(MOCK_CONSULTANTS[0].id);
  const [scheduleStep, setScheduleStep] = React.useState(0);
  const features = useIbProductFeaturesStore((state) => state.features);
  const showWalletCards = isIbProductEnabled(features, "cards");

  const faqData: FaqCat[] = React.useMemo(
    () => [
      {
        title: "Pix",
        description: "Transferências, copia e cola, limites e vendas.",
        items: [
          { question: "Como faço uma transferência Pix?", to: "/pix?newTransfer=true", secondary: "Ir para Transferência Pix" },
          { question: "Como pagar com Pix copia e cola?", to: "/pix/copia-cola", secondary: "Pix copia e cola" },
          { question: "Onde acompanho minhas vendas Pix?", to: "/pix/detalhes", secondary: "Gestão de vendas Pix" },
          { question: "Há limites e prazos no Pix?", to: "/pix", secondary: "Ver regras na área Pix" },
        ],
      },
      {
        title: "Transferências",
        description: "Envie valores e acompanhe o status.",
        items: [
          { question: "Como realizar uma transferência?", to: "/?transfer=1", secondary: "Abrir transferência" },
          { question: "Posso agendar transferências?", to: "/?transfer=1", secondary: "Ver opções na transferência" },
        ],
      },
      {
        title: "Pagamentos",
        description: "Boletos, compensação e comprovantes.",
        items: [
          { question: "Como pagar um boleto?", to: "/pagamento", secondary: "Ir para Pagamentos" },
          { question: "Quais os prazos de compensação?", to: "/pagamento", secondary: "Informações na confirmação" },
          { question: "Como acessar meu comprovante?", to: "/comprovantes", secondary: "Ir para Comprovantes" },
        ],
      },
      {
        title: "Conta",
        description: showWalletCards
          ? "Perfil, extrato, cartões e acesso."
          : "Perfil, extrato e acesso.",
        items: [
          { question: "Onde vejo um resumo do meu saldo?", to: "/", secondary: "Acesse o Início" },
          { question: "Como consultar meu extrato?", to: "/extrato", secondary: "Ir para Extrato" },
          { question: "Onde altero meus dados?", to: "/perfil", secondary: "Ir para Meu Perfil" },
          ...(showWalletCards
            ? [{ question: "Onde vejo meu cartão?", to: "/cartoes", secondary: "Cartões" }]
            : []),
          { question: "Como redefinir minha senha?", secondary: "Acesse Meu Perfil e siga as instruções de segurança" },
        ],
      },
      {
        title: "Boletos",
        description: "Emissão e gestão de boletos.",
        items: [
          { question: "Como criar um novo boleto?", to: "/boletos/add", secondary: "Criar Boleto" },
          { question: "Onde vejo meus boletos?", to: "/boletos", secondary: "Listagem de Boletos" },
        ],
      },
    ],
    [showWalletCards]
  );

  const filteredFaq = React.useMemo(() => {
    const q = faqQuery.trim().toLowerCase();
    if (!q) return faqData;
    return faqData
      .map((cat) => {
        const matchesCat = [cat.title, cat.description || ""].some((s) => s.toLowerCase().includes(q));
        const items = cat.items.filter((it) =>
          [it.question, it.secondary || ""].some((s) => s.toLowerCase().includes(q))
        );
        return matchesCat ? cat : { ...cat, items };
      })
      .filter(
        (cat) =>
          cat.items.length > 0 ||
          (cat.description && cat.description.toLowerCase().includes(q)) ||
          cat.title.toLowerCase().includes(q)
      );
  }, [faqData, faqQuery]);

  const goToAtendimento = React.useCallback(() => {
    setScheduleStep(0);
    setTab(1);
  }, []);

  const handleScheduleMeeting = () => {
    const consultant = MOCK_CONSULTANTS.find((c) => c.id === selectedConsultant);
    window.alert(
      `Solicitação enviada! ${consultant?.name} entrará em contato para confirmar sua reunião privada.`
    );
    setScheduleStep(0);
  };

  return (
    <div className="w-full">
      <ScreenGreeting title="Suporte" subTitle="Como podemos ajudar?" />

      <div className="pix-transfer-segment mb-5" role="tablist" aria-label="Abas de suporte">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 0}
          className={`pix-transfer-segment__btn ${tab === 0 ? "pix-transfer-segment__btn--active" : ""}`}
          onClick={() => setTab(0)}
        >
          Ajuda (FAQ)
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 1}
          className={`pix-transfer-segment__btn ${tab === 1 ? "pix-transfer-segment__btn--active" : ""}`}
          onClick={goToAtendimento}
        >
          Atendimento
        </button>
      </div>

      {tab === 0 && (
        <div className="space-y-5">
          <section>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold text-brand-dark">Dúvidas frequentes</h2>
                <p className="mt-0.5 text-xs text-gray-500">
                  Encontre respostas rápidas e acesse as telas principais.
                </p>
              </div>
              <button type="button" className="btn-primary" onClick={goToAtendimento}>
                <TbHeadset className="h-4 w-4" aria-hidden />
                Ir para Atendimento
              </button>
            </div>

            <div className="relative mb-3 max-w-lg">
              <TbSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                className="valut-input py-2.5 pl-9 pr-9"
                placeholder="Buscar no FAQ"
                value={faqQuery}
                onChange={(e) => setFaqQuery(e.target.value)}
                aria-label="Buscar no FAQ"
              />
              {faqQuery && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-brand-cream hover:text-brand-dark"
                  onClick={() => setFaqQuery("")}
                  aria-label="Limpar busca"
                >
                  <TbX className="h-4 w-4" />
                </button>
              )}
            </div>

            {filteredFaq.length === 0 ? (
              <div className="app-card p-4 text-sm text-gray-500">
                Nenhum resultado encontrado para &ldquo;{faqQuery}&rdquo;.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredFaq.map((cat) => (
                  <div key={cat.title} className="app-card overflow-hidden">
                    <details className="group">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3.5 text-left transition hover:bg-gray-50/80 sm:px-5 [&::-webkit-details-marker]:hidden">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-brand-dark">{cat.title}</p>
                          {cat.description && (
                            <p className="mt-0.5 text-xs text-gray-500">{cat.description}</p>
                          )}
                        </div>
                        <TbChevronDown
                          className="h-4 w-4 shrink-0 text-gray-400 transition group-open:rotate-180"
                          aria-hidden
                        />
                      </summary>
                      <div className="border-t border-gray-100 px-4 py-3 sm:px-5">
                        <ul className="space-y-3">
                          {cat.items.map((item) => (
                            <li key={item.question} className="border-b border-gray-50 pb-3 last:border-0 last:pb-0">
                              <p className="text-sm font-medium text-brand-dark">{item.question}</p>
                              {item.to ? (
                                <RouterLink
                                  to={item.to}
                                  className="mt-1 inline-block text-xs font-semibold text-[#c09455] hover:underline"
                                >
                                  {item.secondary || "Abrir"}
                                </RouterLink>
                              ) : (
                                item.secondary && (
                                  <p className="mt-1 text-xs text-gray-500">{item.secondary}</p>
                                )
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </details>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {tab === 1 && (
        <div className="space-y-5">
          <section className="individual-account-banner">
            <div className="individual-account-banner__icon">
              <TbCalendar className="h-5 w-5" aria-hidden />
            </div>
            <div className="individual-account-banner__body">
              <p className="individual-account-banner__title">Agende uma reunião privada</p>
              <p className="individual-account-banner__description">
                Converse com um consultor exclusivo para tirar dúvidas sobre sua conta, Pix e operações.
              </p>
            </div>
            <button
              type="button"
              className="btn-outline-icon shrink-0 self-start"
              aria-label="Informações sobre o atendimento"
              title="Horário de atendimento"
              onClick={() => window.alert("Nosso suporte trabalha em horário comercial de Brasília.")}
            >
              <TbInfoCircle className="h-4 w-4" />
            </button>
          </section>

          <section className="app-card overflow-hidden p-4 md:p-5">
            <div className="mb-4 flex items-center gap-2">
              {[0, 1].map((step) => (
                <React.Fragment key={step}>
                  <div
                    className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                      scheduleStep >= step
                        ? "bg-gradient-to-br from-[#fcd177] to-[#e7a73f] text-brand-dark"
                        : "border border-gray-200 bg-white text-gray-400"
                    }`}
                  >
                    {step + 1}
                  </div>
                  {step === 0 && (
                    <div className={`h-px flex-1 ${scheduleStep >= 1 ? "bg-[#e7a73f]/50" : "bg-gray-200"}`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {scheduleStep === 0 ? (
              <>
                <h2 className="mb-1 text-sm font-bold text-brand-dark">Escolha seu consultor</h2>
                <p className="mb-3 text-xs text-gray-500">Selecione quem irá conduzir sua reunião.</p>
                <div className="grid gap-2 sm:grid-cols-3">
                  {MOCK_CONSULTANTS.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setSelectedConsultant(c.id)}
                      className={`rounded-xl border px-3 py-3 text-left transition ${
                        selectedConsultant === c.id
                          ? "border-[#e7a73f]/50 bg-brand-cream"
                          : "border-gray-100 bg-white hover:border-[#e7a73f]/30 hover:bg-brand-cream/50"
                      }`}
                    >
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-cream text-[#9a7340]">
                        <TbUser className="h-4 w-4" aria-hidden />
                      </span>
                      <p className="mt-2 text-sm font-semibold text-brand-dark">{c.name}</p>
                      <p className="text-[11px] text-gray-500">{c.role}</p>
                    </button>
                  ))}
                </div>
                <button type="button" className="btn-primary mt-4" onClick={() => setScheduleStep(1)}>
                  Continuar
                </button>
              </>
            ) : (
              <>
                <h2 className="mb-1 text-sm font-bold text-brand-dark">Confirme o agendamento</h2>
                <p className="mb-3 text-xs text-gray-500">
                  Horário sugerido: próximo dia útil, entre 9h e 18h (horário de Brasília).
                </p>
                <div className="rounded-xl border border-gray-100 bg-brand-surface px-4 py-3 text-sm text-brand-dark">
                  <span className="font-semibold">
                    {MOCK_CONSULTANTS.find((c) => c.id === selectedConsultant)?.name}
                  </span>
                  <span className="text-gray-500">
                    {" "}
                    — {MOCK_CONSULTANTS.find((c) => c.id === selectedConsultant)?.role}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button type="button" className="btn-outline" onClick={() => setScheduleStep(0)}>
                    Voltar
                  </button>
                  <button type="button" className="btn-primary" onClick={handleScheduleMeeting}>
                    Confirmar reunião
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default Support;
